import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API from "src/API2";
import toast from "react-hot-toast";
import { ToggleButton } from "src/Components/BasicComponent";
import "./createservice.css";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcArrow  = () => <Svg><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></Svg>;
const IcStar   = () => <Svg><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></Svg>;
const IcCash   = () => <Svg><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M6 12h.01M18 12h.01" /></Svg>;
const IcDoc    = () => <Svg><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></Svg>;
const IcCheck  = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcUser   = () => <Svg size={14}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const IcKid    = () => <Svg size={14}><circle cx="12" cy="7" r="3" /><path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" /></Svg>;
const IcBed    = () => <Svg size={14}><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;

const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "" || Number(n) === 0) return "Free";
  const v = Math.round(Number(n) || 0);
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " DA";
};

const PRICING_MODES = [
  { key: "NONE",   label: "Free / included", hint: "No additional charge for guests" },
  { key: "ROOM",   label: "Per room",        hint: "Single price billed per room" },
  { key: "PERSON", label: "Per person",      hint: "Different prices for adults & children" },
];

const CreateService = () => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priceType, setPriceType] = useState("NONE");
  const [priceAdult, setPriceAdult] = useState(0);
  const [priceChild, setPriceChild] = useState(0);
  const [priceRoom, setPriceRoom] = useState(0);
  const [defaultService, setDefaultService] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* Reset price fields when pricing mode changes */
  useEffect(() => {
    setPriceAdult(0);
    setPriceChild(0);
    setPriceRoom(0);
  }, [priceType]);

  const canSubmit = !submitting && name.trim().length > 0;

  const post = () => {
    if (!canSubmit) {
      toast.error("Service name is required");
      return;
    }
    setSubmitting(true);
    toast.promise(
      API.post("roomservices", {
        code: name,
        name,
        description,
        price_base: parseFloat(priceRoom) || 0,
        price_adult: parseFloat(priceAdult) || 0,
        price_child: parseFloat(priceChild) || 0,
        isDefault: defaultService,
      })
        .then(() => navigate("/services"))
        .finally(() => setSubmitting(false)),
      {
        success: "Service created",
        error: "Could not create service",
        loading: "Creating service…",
      },
    );
  };

  /* Live preview */
  const previewName = name || "Service name";
  const previewDesc = description || "Describe what guests get with this service.";

  return (
    <div className="sv2-page">
      {/* Hero */}
      <header className="sv2-hero">
        <button type="button" className="sv2-back" onClick={() => navigate("/services")}>
          <IcArrow /> Back to services
        </button>
        <div className="sv2-hero__body">
          <span className="sv2-hero__eyebrow">Property setup</span>
          <h1 className="sv2-hero__title">New service</h1>
          <p className="sv2-hero__sub">
            Add an amenity guests can opt-in to. Set how it's priced — free,
            per room, or per person — and bundle it into rates from the rate page.
          </p>
        </div>
      </header>

      <div className="sv2-grid">
        {/* Form */}
        <div className="sv2-form">
          {/* Identity */}
          <section className="sv2-card">
            <header className="sv2-card__head">
              <span className="sv2-card__icon"><IcStar /></span>
              <div>
                <h3>Identity</h3>
                <p>How the service appears to staff and guests.</p>
              </div>
            </header>
            <div className="sv2-grid-2">
              <label className="sv2-field sv2-field--full">
                <span>Name</span>
                <input
                  placeholder="e.g. Parking, Breakfast, Spa access"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <div className="sv2-field">
                <span>Default service</span>
                <div className="sv2-toggle-row">
                  <ToggleButton state={defaultService} setVal={setDefaultService} />
                  <span className={`sv2-status sv2-status--${defaultService ? "on" : "off"}`}>
                    {defaultService ? "Auto-bundled" : "Opt-in"}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Pricing mode */}
          <section className="sv2-card">
            <header className="sv2-card__head">
              <span className="sv2-card__icon sv2-card__icon--green"><IcCash /></span>
              <div>
                <h3>Pricing</h3>
                <p>Choose how this service is billed, then set the amount.</p>
              </div>
            </header>

            <div className="sv2-modes">
              {PRICING_MODES.map((m) => (
                <button
                  type="button"
                  key={m.key}
                  className={`sv2-mode ${priceType === m.key ? "is-on" : ""}`}
                  onClick={() => setPriceType(m.key)}
                >
                  <span className="sv2-mode__label">{m.label}</span>
                  <span className="sv2-mode__hint">{m.hint}</span>
                </button>
              ))}
            </div>

            {priceType === "ROOM" && (
              <div className="sv2-grid-2">
                <label className="sv2-field sv2-field--full">
                  <span><IcBed /> Price per room</span>
                  <div className="sv2-input-money">
                    <input
                      type="number"
                      min={0}
                      placeholder="1500"
                      value={priceRoom}
                      onChange={(e) => setPriceRoom(e.target.valueAsNumber || 0)}
                    />
                    <span className="sv2-input-money__unit">DA</span>
                  </div>
                </label>
              </div>
            )}

            {priceType === "PERSON" && (
              <div className="sv2-grid-2">
                <label className="sv2-field">
                  <span><IcUser /> Per adult</span>
                  <div className="sv2-input-money">
                    <input
                      type="number"
                      min={0}
                      placeholder="1200"
                      value={priceAdult}
                      onChange={(e) => setPriceAdult(e.target.valueAsNumber || 0)}
                    />
                    <span className="sv2-input-money__unit">DA</span>
                  </div>
                </label>
                <label className="sv2-field">
                  <span><IcKid /> Per child</span>
                  <div className="sv2-input-money">
                    <input
                      type="number"
                      min={0}
                      placeholder="450"
                      value={priceChild}
                      onChange={(e) => setPriceChild(e.target.valueAsNumber || 0)}
                    />
                    <span className="sv2-input-money__unit">DA</span>
                  </div>
                </label>
              </div>
            )}
          </section>

          {/* Description */}
          <section className="sv2-card">
            <header className="sv2-card__head">
              <span className="sv2-card__icon sv2-card__icon--orange"><IcDoc /></span>
              <div>
                <h3>Description</h3>
                <p>Shown to guests on the booking site.</p>
              </div>
            </header>
            <label className="sv2-field">
              <span>Description</span>
              <textarea
                rows={4}
                placeholder="Safe parking — covered area, 24/7 access…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="sv2-counter">{description.length} characters</span>
            </label>
          </section>
        </div>

        {/* Sticky preview */}
        <aside className="sv2-summary">
          <div className="sv2-summary__card">
            <span className="sv2-summary__eyebrow">Live preview</span>
            <h3>How it will look</h3>

            <div className="sv2-preview">
              <header className="sv2-preview__head">
                <span className="sv2-preview__icon"><IcStar /></span>
                <div>
                  <strong>{previewName}</strong>
                  {defaultService && <span className="sv2-status sv2-status--on">Auto-bundled</span>}
                </div>
              </header>
              <p className="sv2-preview__desc">{previewDesc}</p>

              <div className="sv2-preview__pricing">
                {priceType === "NONE" && (
                  <span className="sv2-pchip sv2-pchip--free">Free / included</span>
                )}
                {priceType === "ROOM" && (
                  <span className="sv2-pchip sv2-pchip--room"><IcBed /> {fmtMoney(priceRoom)} per room</span>
                )}
                {priceType === "PERSON" && (
                  <>
                    <span className="sv2-pchip sv2-pchip--person"><IcUser /> {fmtMoney(priceAdult)} adult</span>
                    <span className="sv2-pchip sv2-pchip--person"><IcKid /> {fmtMoney(priceChild)} child</span>
                  </>
                )}
              </div>
            </div>

            <div className="sv2-summary__actions">
              <button
                type="button"
                className="sv2-btn sv2-btn--ghost"
                onClick={() => navigate("/services")}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sv2-btn sv2-btn--primary"
                onClick={post}
                disabled={!canSubmit}
              >
                {submitting ? "Creating…" : (<><IcCheck /> Create service</>)}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CreateService;
